import React from 'react';
import { CancellationQuestionProps } from '../../types/cancellation';
import { StanTextArea } from '@/components';
import { QUESTION_TYPES, SPECIAL_ANSWERS, TEXTAREA_ROWS } from '../../constants/cancellation';
import { useCancellationQuestion } from '../../hooks/useCancellationQuestion';
import { CancellationCheckbox } from './CancellationCheckbox';

export const CancellationQuestion = ({ question, answer, onAnswer }: CancellationQuestionProps) => {
    const {
        handleSelectOption,
        handleMultiSelectToggle,
        handleTextChange,
        handleOtherTextChange,
        getOtherText,
        isOtherSelectedForSelect,
        isOtherSelectedForMultiSelect,
        getSelectedDbMaps,
    } = useCancellationQuestion({ question, answer, onAnswer });

    // Single select - use buttons
    if (question.type === QUESTION_TYPES.SELECT) {
        const currentSelection =
            typeof answer === 'string' && answer.startsWith(`${SPECIAL_ANSWERS.OTHER}: `)
                ? SPECIAL_ANSWERS.OTHER
                : answer;

        return (
            <div className='space-y-4'>
                <div className='mb-6 pt-6 text-center'>
                    <h2 className='text-heading-heading2 leading-tight'>{question.questionText}</h2>
                    {question.questionDescription && (
                        <p className='text-body-1-regular text-text-secondary mt-4'>{question.questionDescription}</p>
                    )}
                </div>
                <div className='flex flex-col gap-3'>
                    {question.options?.map(option => {
                        const isSelected = currentSelection === option.dbMap;
                        return (
                            <CancellationCheckbox
                                key={option.dbMap}
                                name={`survey-option-${option.dbMap}`}
                                label={option.displayName}
                                checked={isSelected}
                                onChange={() => handleSelectOption(option.dbMap)}
                            />
                        );
                    })}
                </div>
                {isOtherSelectedForSelect && (
                    <div className='mt-4'>
                        <StanTextArea
                            id='survey-other-input'
                            name='survey-other-input'
                            placeholder='Please specify...'
                            value={getOtherText()}
                            onChange={handleOtherTextChange}
                            rows={TEXTAREA_ROWS.OTHER_INPUT}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Multi-select - use buttons (same design as single select)
    if (question.type === QUESTION_TYPES.MULTI_SELECT) {
        const selectedDbMaps = getSelectedDbMaps();

        return (
            <div className='space-y-4'>
                <div className='mb-6 pt-6 text-center'>
                    <h2 className='text-title-h2 text-text-primary leading-tight font-extrabold'>
                        {question.questionText}
                    </h2>
                    {question.questionDescription && (
                        <p className='text-body-1-regular text-text-secondary mt-2'>{question.questionDescription}</p>
                    )}
                </div>
                <div className='flex flex-col gap-3'>
                    {question.options?.map(option => {
                        const isSelected = selectedDbMaps.includes(option.dbMap);
                        return (
                            <CancellationCheckbox
                                key={option.dbMap}
                                name={`survey-option-${option.dbMap}`}
                                label={option.displayName}
                                checked={isSelected}
                                onChange={() => handleMultiSelectToggle(option.dbMap)}
                            />
                        );
                    })}
                </div>
                {isOtherSelectedForMultiSelect && (
                    <div className='mt-4'>
                        <StanTextArea
                            id='survey-other-input-multi'
                            name='survey-other-input-multi'
                            placeholder='Please specify...'
                            value={getOtherText()}
                            onChange={handleOtherTextChange}
                            rows={TEXTAREA_ROWS.OTHER_INPUT}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Text input
    if (question.type === QUESTION_TYPES.TEXT) {
        return (
            <div className='space-y-4'>
                <div className='mb-6 pt-6 text-center'>
                    <h2 className='text-title-h2 text-text-primary leading-tight font-extrabold'>
                        {question.questionText}
                    </h2>
                    {question.questionDescription && (
                        <p className='text-body-1-regular text-text-secondary mt-2'>{question.questionDescription}</p>
                    )}
                </div>
                <StanTextArea
                    id='survey-text-answer'
                    name='survey-text-answer'
                    placeholder={question.options?.[0]?.displayName || 'Enter your answer...'}
                    value={typeof answer === 'string' ? answer : ''}
                    onChange={handleTextChange}
                    rows={TEXTAREA_ROWS.MAIN_TEXT}
                />
            </div>
        );
    }

    return null;
};
